



import React, { useEffect, useState } from 'react';
import { Row, Col, Button, Spinner, Badge, Container } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { BASE_URL } from '../../services/Api';
import { authHeader } from '../../services/auth-header';
import { FaArrowLeft, FaEdit, FaTrash, FaCheckCircle, FaTimesCircle, FaSpinner, FaCalendarAlt, FaUser, FaClipboardCheck } from 'react-icons/fa';

function PumpProductionProcessView() {
    const { id, pointindex } = useParams();
    const navigate = useNavigate();
    const [record, setRecord] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await axios.get(`${BASE_URL}productionProcessPump/byproduction/${id}`, {
                    headers: authHeader(),
                });

                const data = Array.isArray(res.data) ? res.data[0] : null;
                setRecord(data);
            } catch (err) {
                console.error('Error fetching data', err);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to fetch production process data.',
                });
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN');
    };

    const handleDelete = async () => {
        const confirm = await Swal.fire({
            title: 'Are you sure?',
            text: 'You will not be able to recover this entry!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        });

        if (confirm.isConfirmed) {
            try {
                await axios.delete(`${BASE_URL}productionprocess/${id}`, {
                    headers: authHeader(),
                });

                Swal.fire('Deleted!', 'Data has been deleted.', 'success').then(() => {
                    navigate('/pump-production-process-list/1');
                });
            } catch (error) {
                console.error("Error deleting data", error);
                Swal.fire('Error!', 'Failed to delete.', 'error');
            }
        }
    };

    const getStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case 'pending':
                return { bg: 'secondary', icon: <FaTimesCircle className="me-1" size={10} />, text: 'Pending' };
            case 'completed':
                return { bg: 'success', icon: <FaCheckCircle className="me-1" size={10} />, text: 'Completed' };
            case 'inprogress':
            case 'in progress':
                return { bg: 'warning', icon: <FaSpinner className="me-1" size={10} />, text: 'In Progress' };
            case 'accepted':
                return { bg: 'success', icon: <FaCheckCircle className="me-1" size={10} />, text: 'Accepted' };
            case 'rejected':
                return { bg: 'danger', icon: <FaTimesCircle className="me-1" size={10} />, text: 'Rejected' };
            default:
                return { bg: 'light', text: status || '—', icon: null };
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (!record) {
        return (
            <div className="container text-center mt-4">
                <div className="alert alert-warning">No data found...</div>
                <Link to="/pump-production-process-list/1" className="btn btn-secondary btn-sm">
                    <FaArrowLeft className="me-1" /> Back to List
                </Link>
            </div>
        );
    }

    return (
        <Container fluid className="p-3">
            {/* Header Section */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="fw-bold text-primary mb-0">PUMP PRODUCTION PROCESS - VIEW</h5>
                        <div>
                            <Link to={`/pump-production-process-update/${id}/${pointindex}`} className="btn btn-outline-warning btn-sm me-2">
                                <FaEdit className="me-1" />
                                Update
                            </Link>
                            <Button variant="outline-danger" size="sm" className="me-2" onClick={handleDelete}>
                                <FaTrash className="me-1" />
                                Delete
                            </Button>
                            <Link to="/pump-production-process-list/1" className="btn btn-secondary btn-sm">
                                <FaArrowLeft className="me-1" />
                                Back
                            </Link>
                        </div>
                    </div>

                    <Row className="g-2">
                        <Col md={4}>
                            <div className="border-start border-3 border-primary ps-2">
                                <small className="text-muted">Production Record ID</small>
                                <div className="fw-bold">{record.id || '-'}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="border-start border-3 border-success ps-2">
                                <small className="text-muted">Inspection Status</small>
                                <div>
                                    {(() => {
                                        const status = getStatusBadge(record.acceptorreject);
                                        return (
                                            <Badge bg={status.bg} className="px-2 py-1">
                                                {status.icon}
                                                {status.text}
                                            </Badge>
                                        );
                                    })()}
                                </div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="border-start border-3 border-info ps-2">
                                <small className="text-muted">Remarks</small>
                                <div className="fw-bold text-truncate" style={{ maxWidth: '200px' }}>
                                    {record.remarks || '-'}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Stages Status */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">
                        <FaSpinner className="me-2" size={14} />
                        Stages Status
                    </h6>
                </div>
                <div className="p-3">
                    {/* Rough Machining */}
                    <div className="py-2 border-bottom">
                        <Row className="align-items-center">
                            <Col md={3}>
                                <small className="text-muted d-block">Stage</small>
                                <span className="fw-bold">Rough Machining</span>
                            </Col>
                            <Col md={3}>
                                <small className="text-muted d-block">Status</small>
                                {(() => {
                                    const status = getStatusBadge(record.roughmachine);
                                    return (
                                        <Badge bg={status.bg} className="px-2 py-1">
                                            {status.icon}
                                            {status.text}
                                        </Badge>
                                    );
                                })()}
                            </Col>
                            <Col md={3}>
                                <small className="text-muted d-block">Date</small>
                                <span className="fw-bold">
                                    <FaCalendarAlt className="me-2 text-muted" size={12} />
                                    {formatDate(record.roughmachinedate)}
                                </span>
                            </Col>
                        </Row>
                    </div>

                    {/* Fabrication */}
                    <div className="py-2 border-bottom">
                        <Row className="align-items-center">
                            <Col md={3}>
                                <small className="text-muted d-block">Stage</small>
                                <span className="fw-bold">Fabrication</span>
                            </Col>
                            <Col md={3}>
                                <small className="text-muted d-block">Status</small>
                                {(() => {
                                    const status = getStatusBadge(record.fabrication);
                                    return (
                                        <Badge bg={status.bg} className="px-2 py-1">
                                            {status.icon}
                                            {status.text}
                                        </Badge>
                                    );
                                })()}
                            </Col>
                            <Col md={3}>
                                <small className="text-muted d-block">Date</small>
                                <span className="fw-bold">
                                    <FaCalendarAlt className="me-2 text-muted" size={12} />
                                    {formatDate(record.fabricationdate)}
                                </span>
                            </Col>
                        </Row>
                    </div>

                    {/* Final Machining */}
                    <div className="py-2">
                        <Row className="align-items-center">
                            <Col md={3}>
                                <small className="text-muted d-block">Stage</small>
                                <span className="fw-bold">Final Machining</span>
                            </Col>
                            <Col md={3}>
                                <small className="text-muted d-block">Status</small>
                                {(() => {
                                    const status = getStatusBadge(record.finalmachining);
                                    return (
                                        <Badge bg={status.bg} className="px-2 py-1">
                                            {status.icon}
                                            {status.text}
                                        </Badge>
                                    );
                                })()}
                            </Col>
                            <Col md={3}>
                                <small className="text-muted d-block">Date</small>
                                <span className="fw-bold">
                                    <FaCalendarAlt className="me-2 text-muted" size={12} />
                                    {formatDate(record.finalmachiningdate)}
                                </span>
                            </Col>
                        </Row>
                    </div>
                </div>
            </div>

            {/* Inspection Details */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">
                        <FaClipboardCheck className="me-2" size={14} />
                        Inspection Details
                    </h6>
                </div>
                <div className="p-3">
                    {/* Overall Inspection Status */}
                    <div className="mb-3 pb-3 border-bottom">
                        <Row className="align-items-center">
                            <Col md={4}>
                                <small className="text-muted d-block">Inspection Status</small>
                                {(() => {
                                    const status = getStatusBadge(record.acceptorreject);
                                    return (
                                        <Badge bg={status.bg} className="px-3 py-2">
                                            {status.icon}
                                            {status.text}
                                        </Badge>
                                    );
                                })()}
                            </Col>
                            <Col md={8}>
                                <small className="text-muted d-block">Rejection Reason</small>
                                <span className="fw-bold">{record.rejectionreason || '-'}</span>
                            </Col>
                        </Row>
                    </div>

                    {/* Inspector 1 */}
                    <div className="mb-2 pb-2 border-bottom">
                        <Row>
                            <Col md={6}>
                                <small className="text-muted d-block">Inspector 1</small>
                                <span className="fw-bold">
                                    <FaUser className="me-2 text-muted" size={12} />
                                    {record.inspectedbyone || '-'}
                                </span>
                            </Col>
                            <Col md={6}>
                                <small className="text-muted d-block">Date</small>
                                <span className="fw-bold">
                                    <FaCalendarAlt className="me-2 text-muted" size={12} />
                                    {formatDate(record.inspectiondateone)}
                                </span>
                            </Col>
                        </Row>
                    </div>

                    {/* Inspector 2 */}
                    <div className="mb-2 pb-2 border-bottom">
                        <Row>
                            <Col md={6}>
                                <small className="text-muted d-block">Inspector 2</small>
                                <span className="fw-bold">
                                    <FaUser className="me-2 text-muted" size={12} />
                                    {record.inspectedbytwo || '-'}
                                </span>
                            </Col>
                            <Col md={6}>
                                <small className="text-muted d-block">Date</small>
                                <span className="fw-bold">
                                    <FaCalendarAlt className="me-2 text-muted" size={12} />
                                    {formatDate(record.inspectiondatetwo)}
                                </span>
                            </Col>
                        </Row>
                    </div>

                    {/* Inspector 3 */}
                    <div className="mb-2">
                        <Row>
                            <Col md={6}>
                                <small className="text-muted d-block">Inspector 3</small>
                                <span className="fw-bold">
                                    <FaUser className="me-2 text-muted" size={12} />
                                    {record.inspectedbythree || '-'}
                                </span>
                            </Col>
                            <Col md={6}>
                                <small className="text-muted d-block">Date</small>
                                <span className="fw-bold">
                                    <FaCalendarAlt className="me-2 text-muted" size={12} />
                                    {formatDate(record.inspectiondatethree)}
                                </span>
                            </Col>
                        </Row>
                    </div>
                </div>
            </div>

            {/* Remarks */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Remarks</h6>
                </div>
                <div className="p-3">
                    <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                        <span className="fw-bold">{record.remarks || '-'}</span>
                    </div>
                </div>
            </div>

            {/* Footer Actions */}
            <div className="text-center mt-3 pt-2 border-top">
                <Link to={`/pump-production-process-update/${id}/${pointindex}`} className="btn btn-primary btn-sm me-2">
                    <FaEdit className="me-1" />
                    Update Process
                </Link>
                <Link to="/pump-production-process-list/1" className="btn btn-secondary btn-sm">
                    <FaArrowLeft className="me-1" />
                    Back to List
                </Link>
            </div>
        </Container>
    );
}

export default PumpProductionProcessView;